import React, {
  Fragment,
  memo,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Cell,
  Column,
  ColumnInstance,
  useExpanded,
  useSortBy,
  useTable,
} from 'react-table';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import EnhancedMarkdownCenter from '../EnhancedMarkdown/components/EnhancedMarkdownCenter';
import EnhancedMarkdownZmdiIcon from '../EnhancedMarkdown/components/EnhancedMarkdownZmdiIcon';
import EnhancedMarkdownStyled, {
  buildEnhancedMarkdownStyledShorthands,
} from '../EnhancedMarkdown/components/EnhancedMarkdownStyled';
import EnhancedMarkdownActorIcon from '../EnhancedMarkdown/components/EnhancedMarkdownActorIcon';
import EnhancedMarkdownArrow, {
  buildEnhancedMarkdownArrowShortHands,
} from '../EnhancedMarkdown/components/EnhancedMarkdownArrow';
import EnhancedMarkdownEncounterIcon from '../EnhancedMarkdown/components/EnhancedMarkdownEncounterIcon';
import EnhancedMarkdownItemIcon from '../EnhancedMarkdown/components/EnhancedMarkdownItemIcon';
import EnhancedMarkdownBar from '../EnhancedMarkdown/components/EnhancedMarkdownBar';
import useTheme from '../../contexts/useTheme';
import Button, { ButtonStyle } from '../Button/Button';
import EnhancedMarkdownIcon from '../EnhancedMarkdown/components/EnhancedMarkdownIcon';
import EnhancedMarkdownAbilityIcon from '@resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownAbilityIcon';
import EnhancedMarkdownGearIcon from '../EnhancedMarkdown/components/EnhancedMarkdownGearIcon';
import useTableHiddenColumns from '@resources/js/contexts/useTableHiddenColumns';
import { EnhancedMarkdownOptionsContextProvider } from '@resources/js/contexts/useEnhancedMarkdownOptions';
import EnhancedMarkdownTooltip from '@resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownTooltip';
import EnhancedMarkdownBadgeLabel from '@resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownBadgeLabel';
import EnhancedMarkdownSiteLogo from '@resources/js/components/EnhancedMarkdown/components/EnhancedMarkdownSiteLogo';
import HorizontalContent from '@resources/js/components/HorizontalContent/HorizontalContent';
import { renderToStaticMarkup } from 'react-dom/server';

export type TableProps<
  Row extends Record<string, unknown> = Record<string, unknown>
> = {
  style?: TableStyle;
  columns: Record<string, TableColumn<Row> | null | undefined>;
  data: readonly Record<string, unknown>[];
  footer?: Row;
  enhancedMarkdownEnabled?: boolean;
  truncate?: {
    limit?: number;
    showMoreButton: {
      styles: ButtonStyle[] | 'inline-row';
    };
  };
  sorting?: {
    initialColumn: string | null;
    showControls?: boolean;
  };
  onRowClick?: (row: Record<string, unknown>) => void;
  wrapperStyle?: React.CSSProperties;
};

export type TableStyle =
  | 'dark-gray-striped'
  | 'dark-blue-striped'
  | 'report'
  | 'v2'
  | 'v2-bordered';

type SortingDirection = 'asc' | 'desc';

export type TableColumn<
  Row extends Record<string, unknown> = Record<string, unknown>
> = {
  header: string | JSX.Element;
  title?: string;
  width?: number;
  maxWidth?: number;
  minWidth?: number;
  noWrap?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  columns?: Record<string, TableColumn<Row>>;
  renderCell?: (cell: Cell<Row>) => JSX.Element;
  renderFooter?: (column: ColumnInstance<Row>, footer?: Row) => JSX.Element;
  displayValueKey?: string;
  sorting?: {
    initialDirection: SortingDirection;
  };
  expandable?: {
    expanderPosition: 'right';
  };
};

const defaultWrapperStyle = {};

function Table<Row extends Record<string, unknown>>({
  style,
  columns: nullableColumns,
  data,
  footer,
  enhancedMarkdownEnabled = false,
  truncate,
  sorting,
  onRowClick,
  wrapperStyle = defaultWrapperStyle,
}: TableProps<Row>): JSX.Element {
  const { hiddenColumns } = useTableHiddenColumns();

  const columns = useMemo(
    () =>
      Object.entries(nullableColumns).reduce<
        Record<string, NonNullable<TableColumn<Row>>>
      >((acc, [key, value]) => {
        if (!value) {
          return acc;
        }

        const column: TableColumn<Row> = {
          ...value,
          sorting: sorting && value?.sorting,
        };

        acc[key] = column;
        return acc;
      }, {}),
    [nullableColumns, sorting]
  );

  const flatColumns = useMemo(() => toFlatColumns(columns), [columns]);

  const reactTableColumns = useMemo(
    () => toReactTableColumns(columns, footer),
    [columns, footer]
  );

  const [showMore, setShowMore] = useState(false);

  const initialState = useMemo(
    () => ({
      hiddenColumns,
      ...(sorting
        ? {
            sortBy: sorting.initialColumn
              ? [
                  {
                    id: sorting.initialColumn,
                    desc:
                      flatColumns[sorting.initialColumn].sorting
                        ?.initialDirection === 'desc',
                  },
                ]
              : [],
          }
        : {}),
    }),
    [flatColumns, hiddenColumns, sorting]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    setHiddenColumns,
  } = useTable(
    {
      columns: reactTableColumns,
      data,
      // TODO: Migrate to @tanstack/react-table for proper typing
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      initialState,
      // TODO: Migrate to @tanstack/react-table for proper typing
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      disableSortRemove: true,
    },
    useSortBy,
    useExpanded
  );

  useEffect(() => {
    setHiddenColumns(hiddenColumns);
  }, [hiddenColumns, setHiddenColumns]);

  const hiddenRows = useMemo(() => {
    const hiddenRows = new Set();
    if (showMore || !truncate?.limit) {
      return hiddenRows;
    }
    let shownTopLevelRows = 0;
    let lastTopLevelRowWasShown = false;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
      const depth = row.depth ?? 0;
      if (depth > 0 && lastTopLevelRowWasShown) {
        continue;
      }
      if (shownTopLevelRows >= truncate.limit) {
        hiddenRows.add(row);
        lastTopLevelRowWasShown = false;
      } else {
        shownTopLevelRows++;
        lastTopLevelRowWasShown = true;
      }
    }
    return hiddenRows;
  }, [rows, truncate?.limit, showMore]);

  const theme = useTheme();
  style = style ?? theme.tableStyle;
  let className = undefined;
  switch (style) {
    case 'dark-gray-striped':
      className = 'dark-gray-striped-table';
      break;
    case 'dark-blue-striped':
      className = 'dark-striped-table';
      break;
    case 'report':
      className = 'react-table react-table--report';
      break;
    case 'v2':
      className = 'react-table react-table--v2';
      break;
    case 'v2-bordered':
      className = 'react-table react-table--v2 react-table--v2--bordered';
      break;
  }

  const enhancedMarkdownOverrides = useMemo(
    () => getTableEnhancedMarkdownOverrides(style),
    [style]
  );

  const enhancedMarkdownOptions = useMemo(
    () => ({
      lazyLoad: false,
    }),
    []
  );

  const showSortingControls = sorting?.showControls ?? true;

  return (
    <EnhancedMarkdownOptionsContextProvider value={enhancedMarkdownOptions}>
      <div className={'react-table__wrapper'} style={wrapperStyle}>
        <div className={'react-table__overflow-wrapper'}>
          <table className={className} {...getTableProps({ role: undefined })}>
            {Object.values(columns).some((column) => column.header) && (
              <thead>
                {headerGroups.map((headerGroup) => {
                  const headerGroupProps = headerGroup.getHeaderGroupProps({
                    role: undefined,
                  });
                  return (
                    <tr {...headerGroupProps} key={headerGroupProps.key}>
                      {headerGroup.headers.map((column) => {
                        const options = flatColumns[column.id];
                        const headerProps = column.getHeaderProps({
                          ...(options?.sorting
                            ? // TODO: Migrate to @tanstack/react-table for proper typing
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              column.getSortByToggleProps()
                            : undefined),
                          role: undefined,
                          className: `react-table__cell--${column.id} ${
                            options?.noWrap ? 'react-table__cell--no-wrap' : ''
                          }`,
                        });

                        return (
                          <th
                            {...headerProps}
                            key={headerProps.key}
                            style={{
                              ...column.getHeaderProps().style,
                              ...getColumnStyle(true, options),
                              ...{ textAlign: options?.textAlign ?? 'center' },
                            }}
                            title={options?.title}
                          >
                            <Wrapper
                              enhancedMarkdownEnabled={enhancedMarkdownEnabled}
                              enhancedMarkdownOverrides={
                                enhancedMarkdownOverrides
                              }
                            >
                              {column.render('Header')}
                              {options?.sorting && showSortingControls ? (
                                // TODO: Migrate to @tanstack/react-table for proper typing
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                column.isSorted ? (
                                  // TODO: Migrate to @tanstack/react-table for proper typing
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  // @ts-ignore
                                  column.isSortedDesc ? (
                                    <AscendingSortIcon style={style} />
                                  ) : (
                                    <DescendingSortIcon style={style} />
                                  )
                                ) : (
                                  <span>
                                    <UnsortedIcon style={style} />
                                  </span>
                                )
                              ) : null}
                            </Wrapper>
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
            )}
            <tbody {...getTableBodyProps({ role: undefined })}>
              {rows.map((row) => {
                prepareRow(row);
                const rowProps = row.getRowProps({ role: undefined });
                return (
                  <tr
                    {...rowProps}
                    key={rowProps.key}
                    hidden={hiddenRows.has(row)}
                    onClick={
                      onRowClick ? () => onRowClick(row.original) : undefined
                    }
                    className={onRowClick ? 'react-table__row--selectable' : ''}
                  >
                    {row.cells.map((cell) => {
                      const options = flatColumns[cell.column.id];
                      const cellProps = cell.getCellProps([
                        {
                          role: undefined,
                          className: `react-table__cell--${cell.column.id} ${
                            options?.noWrap ? 'react-table__cell--no-wrap' : ''
                          }`,
                        },
                      ]);

                      return (
                        <td
                          {...cellProps}
                          key={cellProps.key}
                          style={{
                            ...cellProps.style,
                            ...getColumnStyle(false, options),
                            ...{
                              textAlign: options?.textAlign ?? 'left',
                              paddingLeft: options.expandable
                                ? // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
                                  row.depth * 26
                                : undefined,
                            },
                          }}
                        >
                          {
                            // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
                            cell.row.canExpand &&
                            options.expandable?.expanderPosition === 'right' ? (
                              <HorizontalContent
                                style={{ justifyContent: 'space-between' }}
                              >
                                <Wrapper
                                  enhancedMarkdownEnabled={
                                    enhancedMarkdownEnabled
                                  }
                                  enhancedMarkdownOverrides={
                                    enhancedMarkdownOverrides
                                  }
                                >
                                  {(
                                    cell.column.Cell as (
                                      cell: unknown
                                    ) => JSX.Element
                                  )(cell)}
                                </Wrapper>
                                <Button
                                  styles={['unstyled']}
                                  onClick={() =>
                                    // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
                                    cell.row.toggleRowExpanded(
                                      // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
                                      !cell.row.isExpanded
                                    )
                                  }
                                >
                                  <AscendingSortIcon style={'report'} />
                                </Button>
                              </HorizontalContent>
                            ) : (
                              <Wrapper
                                enhancedMarkdownEnabled={
                                  enhancedMarkdownEnabled
                                }
                                enhancedMarkdownOverrides={
                                  enhancedMarkdownOverrides
                                }
                              >
                                {(
                                  cell.column.Cell as (
                                    cell: unknown
                                  ) => JSX.Element
                                )(cell)}
                              </Wrapper>
                            )
                          }
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {truncate?.limit &&
                data.length > truncate.limit &&
                truncate.showMoreButton.styles === 'inline-row' && (
                  <tr>
                    <td
                      colSpan={1000}
                      onClick={() => setShowMore(!showMore)}
                      className={'react-table__truncate-row'}
                    >
                      {showMore
                        ? trans('global.show-less')
                        : trans('global.show-more')}
                    </td>
                  </tr>
                )}
            </tbody>
            {footer && (
              <tfoot>
                {footerGroups.map((group) => {
                  const footerGroupProps = group.getFooterGroupProps();
                  return (
                    <tr {...footerGroupProps} key={footerGroupProps.key}>
                      {group.headers.map((column) => {
                        const options = flatColumns[column.id];
                        const footerProps = column.getFooterProps([
                          {
                            role: undefined,
                            className: `react-table__cell--${column.id} ${
                              options?.noWrap
                                ? 'react-table__cell--no-wrap'
                                : ''
                            }`,
                          },
                        ]);

                        return (
                          <td
                            {...footerProps}
                            key={footerProps.key}
                            style={{
                              ...footerProps.style,
                              ...getColumnStyle(false, options),
                              ...{ textAlign: options?.textAlign ?? 'left' },
                            }}
                          >
                            <Wrapper
                              enhancedMarkdownEnabled={enhancedMarkdownEnabled}
                              enhancedMarkdownOverrides={
                                enhancedMarkdownOverrides
                              }
                            >
                              {(
                                column.Footer as ({
                                  column,
                                }: {
                                  column: unknown;
                                }) => JSX.Element
                              )({ column })}
                            </Wrapper>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tfoot>
            )}
          </table>
        </div>
        {truncate?.limit &&
          data.length > truncate.limit &&
          truncate.showMoreButton.styles !== 'inline-row' && (
            <Button
              onClick={() => setShowMore(!showMore)}
              styles={truncate.showMoreButton.styles}
            >
              {showMore ? trans('global.show-less') : trans('global.show-more')}
            </Button>
          )}
      </div>
    </EnhancedMarkdownOptionsContextProvider>
  );
}

function getTableEnhancedMarkdownOverrides<Row extends Record<string, unknown>>(
  style: TableProps<Row>['style']
) {
  let AbilityIcon = EnhancedMarkdownAbilityIcon;
  let ActorIcon = EnhancedMarkdownActorIcon;
  let ItemIcon = EnhancedMarkdownItemIcon;
  let EncounterIcon = EnhancedMarkdownEncounterIcon;
  let ImageIcon = EnhancedMarkdownIcon;
  let GearIcon = EnhancedMarkdownGearIcon;
  if (style === 'report') {
    // eslint-disable-next-line react/display-name
    AbilityIcon = (props) => (
      <EnhancedMarkdownAbilityIcon rounded={false} {...props} />
    );
    // eslint-disable-next-line react/display-name
    ActorIcon = (props) => (
      <EnhancedMarkdownActorIcon rounded={false} bordered={true} {...props} />
    );
    // eslint-disable-next-line react/display-name
    ItemIcon = (props) => (
      <EnhancedMarkdownItemIcon rounded={false} bordered={true} {...props} />
    );
    // eslint-disable-next-line react/display-name
    EncounterIcon = (props) => (
      <EnhancedMarkdownEncounterIcon
        rounded={false}
        bordered={true}
        {...props}
      />
    );
    // eslint-disable-next-line react/display-name
    ImageIcon = (props) => (
      <EnhancedMarkdownIcon rounded={false} bordered={true} {...props} />
    );
    // eslint-disable-next-line react/display-name
    GearIcon = (props) => (
      <EnhancedMarkdownGearIcon rounded={false} bordered={true} {...props} />
    );
  }

  return {
    Center: EnhancedMarkdownCenter,
    Icon: EnhancedMarkdownZmdiIcon,
    Styled: EnhancedMarkdownStyled,
    Bar: EnhancedMarkdownBar,
    AbilityIcon,
    ActorIcon,
    ...buildEnhancedMarkdownStyledShorthands(),
    ItemIcon,
    EncounterIcon,
    ImageIcon,
    GearIcon,
    Arrow: EnhancedMarkdownArrow,
    Tooltip: EnhancedMarkdownTooltip,
    BadgeLabel: EnhancedMarkdownBadgeLabel,
    SiteLogo: EnhancedMarkdownSiteLogo,
    HorizontalContent,
    ...buildEnhancedMarkdownArrowShortHands(),
  };
}

function getColumnStyle<Row extends Record<string, unknown>>(
  isHeader: boolean,
  options?: TableColumn<Row>
): React.CSSProperties {
  if (!options) {
    return {};
  }

  return {
    width: options.width,
    minWidth: options.minWidth,
    maxWidth: options.maxWidth,
    ...(isHeader && options.sorting
      ? {
          cursor: 'pointer',
          userSelect: 'none',
        }
      : {}),
  };
}

function toFlatColumns<Row extends Record<string, unknown>>(
  columns: Record<string, TableColumn<Row>>
): Record<string, TableColumn<Row>> {
  let flatColumns = { ...columns };
  Object.values(columns).forEach((options) => {
    if (options.columns)
      flatColumns = { ...flatColumns, ...toFlatColumns(options.columns) };
  });
  return flatColumns;
}

const sortType = (
  rowA: { original: Record<string, unknown> },
  rowB: { original: Record<string, unknown> },
  columnId: string
) => {
  const valueA = rowA.original[columnId];
  const valueB = rowB.original[columnId];

  if (typeof valueA === 'string' && typeof valueB === 'string') {
    return valueA.localeCompare(valueB);
  }

  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return valueA - valueB;
  }

  return 0;
};

function toReactTableColumns<Row extends Record<string, unknown>>(
  columns: Record<string, TableColumn<Row>>,
  footer?: Row
): Column<Record<string, unknown>>[] {
  return Object.entries(columns).map(
    ([accessor, options]) =>
      ({
        id: accessor,
        accessor,
        Header: options.header,
        Cell:
          options.renderCell ??
          ((cell: Cell<Row>) =>
            options.displayValueKey
              ? cell.row.original[options.displayValueKey] ?? cell.value
              : cell.value),
        Footer: ({ column }) =>
          options.renderFooter?.(column as ColumnInstance<Row>, footer) ??
          (options.displayValueKey
            ? footer?.[options.displayValueKey]
            : undefined) ??
          footer?.[column.id] ??
          '',
        width: options.width,
        maxWidth: options.maxWidth,
        minWidth: options.minWidth,
        sortDescFirst: options.sorting?.initialDirection === 'desc',
        sortType,
        columns: options.columns
          ? toReactTableColumns(options.columns)
          : undefined,
      } as Column<Record<string, unknown>>)
  );
}

const EnhancedMarkdownWrapper = memo(function EnhancedMarkdownWrapper({
  children,
  enhancedMarkdownOverrides,
}: {
  children: ReactNode;
  enhancedMarkdownOverrides: Record<string, unknown>;
}) {
  if (Array.isArray(children)) {
    // Deal with each child separately
    return (
      <>
        {children.map((child, index) => (
          <EnhancedMarkdownWrapper
            key={index}
            enhancedMarkdownOverrides={enhancedMarkdownOverrides}
          >
            {child}
          </EnhancedMarkdownWrapper>
        ))}
      </>
    );
  }

  let markdown: string | undefined;

  // First check if the child component is a string
  if (typeof children === 'string') {
    markdown = children;
  }

  if (!markdown && markdown !== '') {
    // Else check if the child component has a value prop (i.e. it is a table cell)
    markdown = (
      children as {
        props: { value: string };
      }
    )?.props?.value?.toString();
  }

  if (!markdown && markdown !== '') {
    // Else check if the child type is "Footer" (i.e. it is a footer cell)
    if (
      children &&
      typeof children === 'object' &&
      'type' in children &&
      typeof children.type === 'string' &&
      children.type === 'Footer'
    ) {
      // "Footer" is a react-table internal component, so we render it to get the markdown
      markdown = renderToStaticMarkup(children);
    }
  }

  // If we have no markdown at this point, the child is likely a React component, so just return it
  if (!markdown && markdown !== '') {
    return <>{children}</>;
  }

  return (
    <EnhancedMarkdown
      markdown={markdown}
      wrapped={false}
      overrides={enhancedMarkdownOverrides}
      onlyUsePassedOverrides={true}
    />
  );
});

const Wrapper = memo(function Wrapper({
  children,
  enhancedMarkdownEnabled,
  enhancedMarkdownOverrides,
}: {
  children: ReactNode;
  enhancedMarkdownEnabled: boolean;
  enhancedMarkdownOverrides: Record<string, unknown>;
}) {
  if (enhancedMarkdownEnabled) {
    return (
      <EnhancedMarkdownWrapper
        enhancedMarkdownOverrides={enhancedMarkdownOverrides}
      >
        {children}
      </EnhancedMarkdownWrapper>
    );
  }

  return <>{children}</>;
});

export const UnsortedIcon = memo(function UnsortedIcon({
  style = 'v2',
}: {
  style: TableStyle | undefined;
}) {
  const scale = style === 'report' ? 0.75 : 1;

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='12'
      height='16'
      viewBox='0 0 17 24'
      fill='#808080'
      style={{ marginLeft: 8, marginBottom: style === 'report' ? -5 : -1 }}
    >
      <g transform={`scale(${scale})`}>
        <path d='M8.5 0L0 9H17L8.5 0Z' />
        <path d='M8.5 24L17 15L7.86805e-07 15L8.5 24Z' />
      </g>
    </svg>
  );
});

export const AscendingSortIcon = memo(function AscendingSortIcon({
  style = 'v2',
}: {
  style: TableStyle | undefined;
}) {
  const scale = style === 'report' ? 0.75 : 1;

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='17'
      height='9'
      viewBox='0 0 17 9'
      fill='#DEDEDE'
      style={{ marginLeft: 8, marginBottom: style === 'report' ? 0 : 2 }}
    >
      <g transform={`scale(${scale})`}>
        <path d='M8.5 9L17 1.90735e-06L7.86805e-07 4.21161e-07L8.5 9Z' />
      </g>
    </svg>
  );
});

export const DescendingSortIcon = memo(function DescendingSortIcon({
  style = 'v2',
}: {
  style: TableStyle | undefined;
}) {
  const scale = style === 'report' ? 0.75 : 1;

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='17'
      height='9'
      viewBox='0 0 17 9'
      fill='#DEDEDE'
      style={{ marginLeft: 8, marginBottom: style === 'report' ? 0 : 2 }}
    >
      <g transform={`scale(${scale})`}>
        <path d='M8.5 0L0 9H17L8.5 0Z' />
      </g>
    </svg>
  );
});

export default Table;
